import { useEffect, useMemo, useRef, useState } from 'react'
import type { ECharts } from 'echarts'
import type { ChartLevelAlert } from '@/lib/chartAlerts'
import type { ChartAlertControls } from './ChartLevelAlerts'
import { getRulerPlotRect } from './chartRulerGeometry'

interface AlertScope {
  symbol: string
  metric: 'price' | 'oi'
  market?: 'spot' | 'futures'
  exchanges: readonly string[]
}

interface Preview { id: string; threshold: number; scope: string }

/** Keep drag previews local to this chart; persist once, when the pointer is released. */
export function useChartAlertDrag(chart: ECharts | null, controls: ChartAlertControls | undefined, scope: AlertScope) {
  const [preview, setPreview] = useState<Preview | null>(null)
  const latest = useRef({ controls, scope })
  latest.current = { controls, scope }
  const scopeKey = JSON.stringify([scope.symbol, scope.metric, scope.market || 'futures', scope.exchanges])
  const enabled = !!(controls?.onUpdate || controls?.onDelete)

  useEffect(() => {
    setPreview(null)
    if (!chart || chart.isDisposed() || !enabled) return
    const dom = chart.getDom()
    const zr = chart.getZr()
    let disposed = false
    let drag: { alert: ChartLevelAlert; pointerId?: number; startY: number; offsetY: number; threshold: number; moved: boolean } | null = null
    let saving = false
    let suppressUntil = 0
    let suppressContextMenu = false
    let cursorActive = false
    let previousCursor = ''
    const validAlerts = () => {
      const { controls: current, scope: context } = latest.current
      return (current?.alerts || []).filter((alert) => alert.status === 'active'
        && alert.symbol.toUpperCase() === context.symbol.toUpperCase()
        && alert.metric === context.metric && (alert.market || 'futures') === (context.market || 'futures')
        && context.exchanges.some((exchange) => exchange.toLowerCase() === alert.exchange.toLowerCase())
        && Number.isFinite(alert.threshold) && alert.threshold > 0)
    }
    const cursor = (active: boolean) => {
      if (active && !cursorActive) previousCursor = dom.style.cursor
      if (active || cursorActive) {
        dom.style.cursor = active ? 'ns-resize' : previousCursor
        if (!chart.isDisposed()) zr.setCursorStyle(active ? 'ns-resize' : previousCursor)
      }
      cursorActive = active
    }
    const suppress = (event: Event) => { event.preventDefault(); event.stopImmediatePropagation() }
    const point = (event: MouseEvent) => {
      const rect = dom.getBoundingClientRect()
      return [(event.clientX - rect.left) * chart.getWidth() / (rect.width || 1),
        (event.clientY - rect.top) * chart.getHeight() / (rect.height || 1)]
    }
    const modified = (event: MouseEvent) => event.shiftKey || event.altKey || event.ctrlKey || event.metaKey
    const hit = (event: MouseEvent) => {
      if (saving || latest.current.controls?.isSaving || modified(event)) return null
      const pixel = point(event)
      if (!pixel.every(Number.isFinite) || !chart.containPixel({ gridIndex: 0 }, pixel)) return null
      let nearest: { alert: ChartLevelAlert; y: number; distance: number } | null = null
      for (const alert of validAlerts()) {
        const y = chart.convertToPixel({ yAxisIndex: 0 }, alert.threshold) as number
        const distance = Math.abs(y - pixel[1])
        if (Number.isFinite(y) && distance <= 6 && (!nearest || distance < nearest.distance)) nearest = { alert, y, distance }
      }
      return nearest
    }
    const cancel = () => {
      if (drag) suppressUntil = Date.now() + 350
      drag = null
      setPreview(null)
      cursor(false)
    }
    const cancelWithRightClick = (event: MouseEvent) => {
      suppress(event)
      suppressContextMenu = true
      cancel()
    }
    const secondaryDown = (event: MouseEvent) => {
      if (drag && event.button === 2) cancelWithRightClick(event)
    }
    const contextMenu = (event: MouseEvent) => {
      if (!drag && !suppressContextMenu) {
        const remove = latest.current.controls?.onDelete
        if (!remove || chart.isDisposed() || event.defaultPrevented || !dom.contains(event.target as Node)) return
        const nearest = hit(event)
        if (!nearest) return
        suppress(event)
        cursor(false)
        saving = true
        // The mutation reports failures and retains the original line until the
        // server confirms cancellation, just like deleting from My alerts.
        void Promise.resolve().then(() => remove(nearest.alert.id)).catch(() => {}).finally(() => {
          if (!disposed) saving = false
        })
        return
      }
      suppress(event)
      suppressContextMenu = false
      if (drag) cancel()
    }
    const stillActive = () => !drag || validAlerts().some((alert) => alert.id === drag!.alert.id && alert.threshold === drag!.alert.threshold)
    const down = (event: MouseEvent) => {
      if (chart.isDisposed() || event.defaultPrevented || event.button !== 0 || drag || !latest.current.controls?.onUpdate) return
      const nearest = hit(event)
      if (!nearest) return
      const pixel = point(event)
      drag = { alert: nearest.alert, startY: event.clientY, offsetY: pixel[1] - nearest.y,
        pointerId: 'pointerId' in event ? (event as PointerEvent).pointerId : undefined,
        threshold: nearest.alert.threshold, moved: false }
      suppressUntil = 0
      suppressContextMenu = false
      suppress(event)
      cursor(true)
      chart.dispatchAction({ type: 'hideTip' }, { silent: true })
    }
    const samePointer = (event: MouseEvent) => drag?.pointerId == null || !('pointerId' in event) || (event as PointerEvent).pointerId === drag.pointerId
    const update = (event: MouseEvent) => {
      if (!drag) return
      if (!stillActive()) { cancel(); return }
      if (Math.abs(event.clientY - drag.startY) >= 3) drag.moved = true
      if (!drag.moved) return
      const plot = getRulerPlotRect(chart)
      if (!plot) return
      const y = Math.max(plot.y, Math.min(plot.y + plot.height, point(event)[1] - drag.offsetY))
      const value = chart.convertFromPixel({ yAxisIndex: 0 }, y) as number
      if (!Number.isFinite(value) || value <= 0) return
      drag.threshold = Number(value.toPrecision(12))
      setPreview({ id: drag.alert.id, threshold: drag.threshold, scope: scopeKey })
    }
    const move = (event: MouseEvent) => {
      if (chart.isDisposed() || !drag || !samePointer(event)) return
      // Pressing a second mouse button during a pointer drag can produce a
      // pointermove instead of another pointerdown.
      if (event.buttons & 2) { cancelWithRightClick(event); return }
      suppress(event)
      if (event.buttons === 0) { cancel(); return }
      update(event)
      cursor(!!drag)
    }
    const hover = (event: MouseEvent) => {
      if (!chart.isDisposed() && !drag) cursor(!!latest.current.controls?.onUpdate && !!hit(event))
    }
    const up = (event: MouseEvent) => {
      if (chart.isDisposed() || !drag || !samePointer(event)) return
      if (event.button === 2 || (event.buttons & 2)) { cancelWithRightClick(event); return }
      if (event.button !== 0) return
      suppress(event)
      update(event)
      const finished = drag
      drag = null
      suppressUntil = Date.now() + 350
      cursor(false)
      if (!finished?.moved || finished.threshold === finished.alert.threshold) { setPreview(null); return }
      const save = latest.current.controls?.onUpdate
      if (!save) { setPreview(null); return }
      saving = true
      // onUpdate reports server errors to the user. Always clear the local preview,
      // revealing either the canonical saved alert or its unchanged original value.
      void Promise.resolve().then(() => save(finished.alert.id, finished.threshold)).catch(() => {}).finally(() => {
        if (!disposed) { saving = false; setPreview(null) }
      })
    }
    const click = (event: MouseEvent) => {
      if (drag || Date.now() < suppressUntil || (!chart.isDisposed() && hit(event))) suppress(event)
    }
    const compatibility = (event: MouseEvent) => { if (drag || Date.now() < suppressUntil) suppress(event) }
    const wheel = (event: WheelEvent) => { if (drag) suppress(event) }
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape' && drag) { suppress(event); cancel() } }
    const blur = () => { if (!saving) cancel() }
    const leave = () => { if (!drag) cursor(false) }
    const refresh = () => { if (drag && !stillActive()) cancel() }
    const pointer = typeof PointerEvent !== 'undefined'
    const downName = pointer ? 'pointerdown' : 'mousedown'
    const moveName = pointer ? 'pointermove' : 'mousemove'
    const upName = pointer ? 'pointerup' : 'mouseup'
    dom.addEventListener(downName, down, true)
    // Run hover after zrender sets its own viewport cursor. Drag events are
    // captured separately so they never reach chart panning or the ruler.
    dom.addEventListener(moveName, hover)
    dom.addEventListener('mouseleave', leave)
    dom.addEventListener('click', click, true)
    dom.addEventListener('dblclick', click, true)
    dom.addEventListener('wheel', wheel, { capture: true, passive: false })
    if (pointer) for (const name of ['mousedown', 'mousemove', 'mouseup'] as const) dom.addEventListener(name, compatibility, true)
    window.addEventListener(moveName, move, true)
    window.addEventListener(downName, secondaryDown, true)
    if (pointer) window.addEventListener('mousedown', secondaryDown, true)
    window.addEventListener(upName, up, true)
    window.addEventListener('contextmenu', contextMenu, true)
    window.addEventListener('keydown', key, true)
    window.addEventListener('pointercancel', blur)
    window.addEventListener('blur', blur)
    chart.on('rendered', refresh)
    return () => {
      disposed = true
      cursor(false)
      dom.removeEventListener(downName, down, true)
      dom.removeEventListener(moveName, hover)
      dom.removeEventListener('mouseleave', leave)
      dom.removeEventListener('click', click, true)
      dom.removeEventListener('dblclick', click, true)
      dom.removeEventListener('wheel', wheel, true)
      if (pointer) for (const name of ['mousedown', 'mousemove', 'mouseup'] as const) dom.removeEventListener(name, compatibility, true)
      window.removeEventListener(moveName, move, true)
      window.removeEventListener(downName, secondaryDown, true)
      if (pointer) window.removeEventListener('mousedown', secondaryDown, true)
      window.removeEventListener(upName, up, true)
      window.removeEventListener('contextmenu', contextMenu, true)
      window.removeEventListener('keydown', key, true)
      window.removeEventListener('pointercancel', blur)
      window.removeEventListener('blur', blur)
      chart.off('rendered', refresh)
    }
  }, [chart, enabled, scopeKey])

  return useMemo(() => controls && preview?.scope === scopeKey ? {
    ...controls,
    alerts: controls.alerts.map((alert) => alert.id === preview.id ? { ...alert, threshold: preview.threshold } : alert),
  } : controls, [controls, preview, scopeKey])
}
